import { AxiosError } from 'axios';
import { useGlobalContext } from 'GlobalContext';
import moment from 'moment';
import { useCustomerContext } from 'pages/Customer/CustomerContext';
import { getFeesFromDriver } from 'pages/Customer/tabs/App/cards/IntegratedPayment/helpers/feesMapping';
import getPixDriver from 'pages/Customer/tabs/App/cards/IntegratedPayment/helpers/getPixDriver';
import { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { listTaxes } from 'services/additionalTax';
import {
  addContractServices,
  createContract,
  listContractsByParams,
} from 'services/contract';
import { getContractTypesCached } from 'services/contractType.cached';
import { listMarketplaceByIdi } from 'services/integratedPayment';
import {
  AddServicesRequestData,
  ContractService,
  CreateContractRequestData,
} from 'services/interfaces/contract';
import { ContractType } from 'services/interfaces/contractType';
import { Mixpanel } from 'services/mixpanel';
import { loadSoftwaresAndServices } from 'services/software';
import { getClusterPricing } from 'shared/helpers/cluster';
import { R$ } from 'shared/helpers/currency';
import { Contract, IContract } from 'shared/models/contract';
import { Discount } from 'shared/models/discount';
import { Marketplace } from 'shared/models/marketplace';
import { Product } from 'shared/models/product';
import { Service } from 'shared/models/service';
import { Software } from 'shared/models/software';
import { FEES } from 'shared/types/marketplacePayment';
import { PaymentTag } from 'shared/types/payment';
import { Periodicity } from 'shared/types/periodicity';
import {
  isStandardMonthlyContractType,
  isStandardYearlyContractType,
} from '../../../helpers/contractType';
import { ContractTypeOption } from '../../atoms';
import DiscountModal from '../../organisms/DiscountModal';
import CreateContractModal from './ContractModal';

const CLUBE_YOOGA_SERVICE_IDS = [83, 84];

const hasClubeYoogaService = (products: Software[]) =>
  products.some((p) =>
    CLUBE_YOOGA_SERVICE_IDS.includes(p.selectedService?.id as number),
  );

interface CreateContractModalProps {
  isOpen: boolean;
  onClose(): void;
  contract?: Contract;
}

interface CreateContractModalContextData {
  step: number;
  nextStep(): void;
  previousStep(): void;
  product?: Software;
  selectedPlan?: Service;
  setSelectedPlan(p?: Service): void;
  addedProducts: Software[];
  addPlan(p: Service): void;
  removePlan(id: number): void;
  contract?: Contract;
  paymentMethod: 1 | 2;
  setPaymentMethod(m: 1 | 2): void;
  periodicity: Periodicity;
  setPeriodicity(p: Periodicity): void;
  checkPeriodicityConflict(contractType: ContractTypeOption): Promise<boolean>;
  getUpsellPrice(): string;
  getPlanPricesSum(): number;
  paymentDay: number;
  setPaymentDay(n: number): void;
  loading: boolean;
  setLoading(b: boolean): void;
  applyDiscount(n: number, d: Discount): void;
  handleSubmit(): void;
  setDiscountProduct(p: Software): void;
  setStep(n: number): void;
  selectedContractType?: ContractTypeOption;
  setSelectedContractType(t?: ContractTypeOption): void;
}

const Context = createContext<CreateContractModalContextData>(
  {} as CreateContractModalContextData,
);

export const useCreateContractModalContext = () => useContext(Context);

const CreateContractModalContext: React.FC<CreateContractModalProps> = ({
  contract,
  isOpen,
  onClose,
}) => {
  const { customer, apps } = useCustomerContext();
  const { confirm } = useGlobalContext();

  const [step, setStep] = useState<number>(0);
  const [product, setProduct] = useState<Software>();
  const [selectedPlan, setSelectedPlan] = useState<Service>();
  const [addedProducts, setAddedProducts] = useState<Software[]>([]);
  const [periodicity, setPeriodicity] = useState<Periodicity>('monthly');
  const [paymentMethod, setPaymentMethod] = useState<1 | 2>(1);
  const [paymentDay, setPaymentDay] = useState<number>(moment().date());
  const [discountProduct, setDiscountProduct] = useState<Software>();

  const [loading, setLoading] = useState(false);
  const [contractTypes, setContractTypes] = useState<ContractType[]>([]);

  const [selectedContractType, setSelectedContractTypeState] = useState<
    ContractTypeOption | undefined
  >(undefined);

  const nextStep = () => setStep(step + 1);
  const previousStep = () => setStep(step - 1);

  const addPlan = (service: Service) => {
    if (!product) {
      console.error('Software not found');
      toast.error('Não foi possível adicionar o serviço');
      return;
    }
    const newProduct = new Software({ name: product.name });
    newProduct.selectedService = service;
    setAddedProducts([...addedProducts, newProduct]);
    setSelectedPlan(undefined);
  };

  const removePlan = (serviceId: number) => {
    setAddedProducts(
      addedProducts.filter(
        (product) => product.selectedService?.id !== serviceId,
      ),
    );
  };

  const applyDiscount = (id: number, discount: Discount) => {
    setAddedProducts(
      addedProducts.map((product) =>
        product.selectedService?.id === id ? { ...product, discount } : product,
      ),
    );
  };

  const getPlanPricesSum = () => {
    let sum = 0;

    addedProducts.forEach((product) => {
      if (product.selectedService?.value) sum += product.selectedService.value;
    });

    return sum;
  };

  const getUpsellPrice = () => {
    const pricesSum = getPlanPricesSum();

    const clusterPrice = getClusterPricing(pricesSum);

    return R$(clusterPrice) as string;
  };

  const getValidPaymentDay = (paymentDay: number) => {
    const validDays = [5, 10, 15, 20];
    for (const day of validDays) {
      if (paymentDay <= day) {
        return day;
      }
    }
    return 5;
  };

  const enrichPayloadWithClubeYoogaData = async (payload: {
    idi?: string;
    marketplaceFeesOld?: FEES[];
    yoogaAdditionalTaxId?: number;
  }) => {
    if (!hasClubeYoogaService(addedProducts)) return;

    const app = apps.filter((a) => !a.emissorModule)[0];
    if (!app) return;

    payload.idi = String(app.idi);

    try {
      const response = await listMarketplaceByIdi(String(app.idi));
      const marketplace = new Marketplace(response.data);
      const driver = getPixDriver(marketplace.drivers);
      payload.marketplaceFeesOld = getFeesFromDriver(driver);
    } catch (e) {
      payload.marketplaceFeesOld = [];
      console.error('Não foi possível obter os dados do marketplace', e);
    }

    try {
      const taxesResponse = await listTaxes(String(app.idi));
      const activeTax = taxesResponse.data.find(
        (tax: { enabled?: boolean }) => tax.enabled === true,
      );
      if (!activeTax) {
        payload.yoogaAdditionalTaxId = 2;
      }
    } catch (e) {
      console.error('Não foi possível obter os dados da taxa adicional', e);
    }
  };

  const handleSubmit = () => {
    if (loading || !addedProducts.length) return;

    if (addedProducts.length < 1) {
      toast.error('Não foi possível adicionar o serviço');
      return;
    }

    const services: ContractService[] = addedProducts
      .map((product) => {
        if (!product.selectedService?.id) {
          console.error(
            'Error adding service [' + product.name + '] missing id',
          );
          toast.error('Não foi possível adicionar o serviço ' + product.name);
          return;
        }
        const service: ContractService = {
          service_id: product.selectedService.id,
        };

        if (product.discount?.discount_value && product.discount?.cycles)
          service.discounts = {
            cycles: product.discount.cycles,
            type: product.discount.discountTypeName,
            value: product.discount.discount_value,
          };

        return service;
      })
      .filter((service): service is ContractService => service !== undefined);

    if (contract && contract.id) {
      confirm('Adicionar serviços', 'Deseja continuar?', async () => {
        Mixpanel.track(
          'customers-financial-add-services-submitted-new-services',
        );
        setLoading(true);

        try {
          const payload: AddServicesRequestData = { services };

          await enrichPayloadWithClubeYoogaData(payload);

          await addContractServices(contract.id as number, payload);
          onClose();
          toast.success('Serviços adicionados');
        } catch (e) {
          const error = e as AxiosError;
          toast.error('Não foi possível adicionar os serviços');
          console.error(
            'Não foi possível adicionar os serviços',
            error?.response?.data
              ? JSON.stringify(error?.response?.data, null, 2)
              : JSON.stringify(error?.response, null, 2),
          );
        } finally {
          setLoading(false);
        }
      });
    } else {
      const dueDate = moment().set('date', getValidPaymentDay(paymentDay));
      if (moment().get('date') > dueDate.get('date')) dueDate.add(1, 'M');

      if (!selectedContractType) {
        console.log(
          'Create contract error: Missing "selectedContractType"',
          selectedContractType,
        );
        toast.error('Não foi possível criar o contrato!');
        return;
      }

      const payload: CreateContractRequestData = {
        customer_id: customer.id,
        due_date: dueDate.format('YYYY-MM-DD'),
        software_id: 1,
        payment_method:
          paymentMethod === 1 ? PaymentTag.CREDIT_CARD : PaymentTag.INVOICE,
        periodicity,
        services,
        contract_type_id: Number(selectedContractType.value),
      };

      confirm('Criar contrato', 'Deseja continuar?', async () => {
        Mixpanel.track('customers-principal-submitted-contract-modal');
        setLoading(true);

        try {
          await enrichPayloadWithClubeYoogaData(payload);

          await createContract(payload);
          onClose();
          toast.success('Contrato criado');
        } catch (e) {
          const error = e as AxiosError;
          toast.error('Não foi possível criar o contrato agora');
          console.error(
            'Não foi possível criar o contrato agora',
            error?.response?.data
              ? JSON.stringify(error?.response?.data, null, 2)
              : JSON.stringify(error?.response, null, 2),
          );
        } finally {
          setLoading(false);
        }
      });
    }
  };

  const handleAddDiscount = (product: Software, discount: Discount) => {
    const productWithDiscount: Software = Object.assign({}, product);
    Object.setPrototypeOf(productWithDiscount, Product.prototype);

    productWithDiscount.discount = discount;

    setAddedProducts(
      addedProducts.map((p) =>
        p.selectedService?.id === product.selectedService?.id
          ? productWithDiscount
          : p,
      ),
    );
  };

  const checkPeriodicityConflict = async (
    selectedType: ContractTypeOption,
  ): Promise<boolean> => {
    const response = await listContractsByParams({
      customer: customer.id,
      status: 1,
      includeDeleted: false,
    });

    const activeContracts = response.data.results.map(
      (c: IContract) => new Contract(c),
    );

    let typesWithRestriction = [];

    if (contractTypes.length) {
      typesWithRestriction = contractTypes.filter(
        (type) => type.has_unique_restriction,
      );

      const contractWithRestriction = [];

      for (const contract of activeContracts) {
        for (const type of typesWithRestriction) {
          if (contract.contract_type_id === type.id) {
            const contractWithType = {
              ...contract,
              type: {
                ...type,
              },
            };
            contractWithRestriction.push(contractWithType);
          }
        }
      }

      for (const contract of contractWithRestriction) {
        if (
          isStandardMonthlyContractType(
            contract.type.validity_duration_unit,
            contract.type.validity_duration_value,
            contract.type.order_cycle_days,
          ) &&
          isStandardMonthlyContractType(
            selectedType.durationValueUnit,
            selectedType.durationValue,
            selectedType.orderCycleDays,
          )
        ) {
          return true;
        }

        if (
          isStandardYearlyContractType(
            contract.type.validity_duration_unit,
            contract.type.validity_duration_value,
            contract.type.order_cycle_days,
          ) &&
          isStandardYearlyContractType(
            selectedType.durationValueUnit,
            selectedType.durationValue,
            selectedType.orderCycleDays,
          )
        ) {
          return true;
        }
      }
    }

    return false;
  };

  const setSelectedContractType = (t?: ContractTypeOption) => {
    setSelectedContractTypeState(t);
    if (!t) return;

    if (
      isStandardMonthlyContractType(
        t.durationValueUnit,
        t.durationValue,
        t.orderCycleDays,
      )
    ) {
      setPeriodicity('monthly');
      return;
    }
    if (
      isStandardYearlyContractType(
        t.durationValueUnit,
        t.durationValue,
        t.orderCycleDays,
      )
    ) {
      setPeriodicity('yearly');
      return;
    }
  };

  useEffect(() => {
    setLoading(true);
    if (!isOpen) return;
    Promise.all([loadSoftwaresAndServices(), getContractTypesCached()])
      .then(([softwaresResponse, normalizedTypes]) => {
        const softwaresData =
          softwaresResponse.data?.results?.map(
            (software) => new Software(software),
          ) ?? [];
        if (softwaresData.length) {
          setProduct(softwaresData[0]);
        } else {
          throw Error('No products retrieved');
        }

        setContractTypes(normalizedTypes);
      })
      .catch((error) => {
        console.error('ModalContext - error loading data:', error);
        toast.error('Não foi possível carregar os dados necessários');
      })
      .finally(() => setLoading(false));
  }, [isOpen]);

  useEffect(() => {
    setSelectedPlan(undefined);
  }, [periodicity, setSelectedPlan]);

  useEffect(() => {
    setStep(contract ? 1 : 0);
    setSelectedPlan(undefined);
    setAddedProducts([]);
    setPeriodicity(contract?.periodicity ?? 'monthly');
    setPaymentMethod(1);
    setPaymentDay(getValidPaymentDay(moment().date()));
    setDiscountProduct(undefined);
    setSelectedContractTypeState(undefined);
  }, [isOpen, contract]);

  const data: CreateContractModalContextData = {
    step,
    nextStep,
    previousStep,
    product,
    selectedPlan,
    setSelectedPlan,
    addedProducts,
    addPlan,
    removePlan,
    contract,
    paymentMethod,
    setPaymentMethod,
    periodicity,
    setPeriodicity,
    checkPeriodicityConflict,
    getUpsellPrice,
    getPlanPricesSum,
    paymentDay,
    setPaymentDay,
    loading,
    setLoading,
    applyDiscount,
    handleSubmit,
    setDiscountProduct,
    setStep,
    selectedContractType,
    setSelectedContractType,
  };

  return (
    <Context.Provider value={data}>
      <CreateContractModal isOpen={isOpen} onClose={onClose} />
      <DiscountModal
        product={discountProduct}
        onClose={() => setDiscountProduct(undefined)}
        onSuccess={handleAddDiscount}
      />
    </Context.Provider>
  );
};

export default CreateContractModalContext;
